import React from 'react';
import { Search, Users, FileCheck, Handshake, Target, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO, { SEO_CONFIGS } from '../components/SEO';

const Methodology = () => {
  const steps = [
    {
      icon: <Target size={32} />,
      title: 'Discovery & Definition',
      description: 'We begin by deeply understanding your organization, culture, strategic goals, and the specific leadership requirements. This phase includes stakeholder interviews and market analysis.'
    },
    {
      icon: <Search size={32} />,
      title: 'Research & Sourcing',
      description: 'Our research team conducts comprehensive market mapping and talent identification across multiple channels, leveraging our extensive network and industry expertise.'
    },
    {
      icon: <Users size={32} />,
      title: 'Assessment & Evaluation',
      description: 'Rigorous multi-stage evaluation process including competency assessments, behavioral interviews, and reference checks to ensure the best fit.'
    },
    {
      icon: <FileCheck size={32} />,
      title: 'Presentation & Selection',
      description: 'We present a curated shortlist of exceptional candidates with detailed profiles and our assessment insights to support your decision-making.'
    },
    {
      icon: <Handshake size={32} />,
      title: 'Offer & Onboarding',
      description: 'We facilitate the offer process and support smooth onboarding to ensure the candidate\'s successful transition into your organization.'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Follow-up & Success',
      description: 'Our engagement continues post-placement with regular check-ins to ensure long-term success and address any transition challenges.'
    }
  ];

  const principles = [
    {
      title: 'Industry Expertise',
      description: 'Deep sector knowledge ensures we understand the unique challenges and requirements of each industry we serve.'
    },
    {
      title: 'Rigorous Process',
      description: 'Our proven methodology combines best practices with tailored approaches for each engagement.'
    },
    {
      title: 'Confidentiality',
      description: 'We maintain the highest standards of discretion and confidentiality throughout the search process.'
    },
    {
      title: 'Long-term Partnership',
      description: 'We build enduring relationships, becoming a trusted advisor for your leadership needs.'
    }
  ];

  return (
    <div>
      <SEO {...SEO_CONFIGS.methodology} />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-primary text-white" data-testid="methodology-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl lg:text-6xl font-bold mb-6"
          >
            Our <span className="gold-text">Methodology</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-300 max-w-3xl"
          >
            A proven, comprehensive approach to executive search that delivers exceptional results
          </motion.p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-spacing bg-white" data-testid="methodology-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-primary mb-4">Our Six-Step Process</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A structured approach ensuring thoroughness and excellence at every stage
            </p>
          </div>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row gap-6 items-start"
                data-testid={`methodology-step-${index}`}
              >
                <div className="flex items-start gap-4 md:w-1/4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 flex-shrink-0">
                    <div className="text-secondary">{step.icon}</div>
                  </div>
                  <div className="text-5xl font-bold text-muted/30">{String(index + 1).padStart(2, '0')}</div>
                </div>
                <div className="md:w-3/4">
                  <h3 className="text-2xl font-bold text-primary mb-3">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section-spacing bg-muted" data-testid="methodology-principles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-primary mb-4">Our Core Principles</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The values that guide our executive search practice
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <div key={index} className="text-center" data-testid={`principle-${index}`}>
                <h3 className="text-xl font-bold text-primary mb-3">{principle.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-primary text-white" data-testid="methodology-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 gold-text">
            Experience Excellence in Executive Search
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Let our proven methodology work for your leadership needs
          </p>
          <a
            href="/contact"
            data-testid="methodology-cta-button"
            className="bg-secondary text-primary hover:bg-secondary/90 px-10 py-5 uppercase tracking-widest text-sm font-semibold transition-all inline-block"
          >
            Start a Conversation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Methodology;